import React, { useEffect, useState, useCallback } from 'react';
import DashboardShell from '@/components/DashboardShell';
import api from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Save, Eye, EyeOff, CheckCircle, XCircle, ChevronRight, Send } from 'lucide-react';

const EVENT_COLORS = {
  welcome:        '#1A7F37',
  approval:       '#1A7F37',
  rejection:      '#B91C1C',
  suspension:     '#B91C1C',
  unsuspension:   '#1A7F37',
  password_reset: '#2563EB',
};

const PREVIEW_VARS = {
  name:       'Jane Smith',
  app_url:    'https://thelistingroom.com',
  reset_url:  'https://thelistingroom.com/reset-password?token=preview',
  admin_note: 'Your portfolio did not meet our current requirements.',
  admin_note_block: '<div style="background:#F5F0E8;border-left:3px solid #C9A84C;padding:12px 16px;border-radius:0 8px 8px 0;margin:16px 0;font-size:14px;color:#5B5B5B;"><strong>Note from our team:</strong> Your portfolio did not meet our current requirements.</div>',
};

function substitute(body, vars) {
  let result = body || '';
  Object.entries(vars).forEach(([k, v]) => {
    result = result.replaceAll(`[[${k}]]`, v);
  });
  return result;
}

function wrap(content) {
  return `<html><body style="font-family:'Georgia',serif;max-width:600px;margin:auto;padding:40px;background:#fff;">
  <div style="border-bottom:2px solid #C9A84C;padding-bottom:20px;margin-bottom:30px;">
    <h1 style="color:#2D2D2D;font-size:22px;margin:0;letter-spacing:-0.02em;">The Listing Room</h1>
    <p style="color:#5B5B5B;font-size:11px;margin:4px 0 0;font-family:'Inter',sans-serif;letter-spacing:0.06em;text-transform:uppercase;">Exclusive Art Marketplace</p>
  </div>
  ${content}
  <div style="border-top:1px solid #E7E1D7;padding-top:20px;margin-top:40px;">
    <p style="color:#999;font-size:11px;font-family:'Inter',sans-serif;">© The Listing Room.</p>
  </div>
</body></html>`;
}

export default function NotificationManager() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', body: '', enabled: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [viewMode, setViewMode] = useState('edit'); // 'edit' | 'preview'

  const fetchList = useCallback(async () => {
    try {
      const r = await api.get('/admin/email-templates');
      setTemplates(r.data);
    } catch { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const selectTemplate = async (key) => {
    if (selected?.key === key) return;
    try {
      const r = await api.get(`/admin/email-templates/${key}`);
      setSelected(r.data);
      setEditForm({ subject: r.data.subject || '', body: r.data.body || '', enabled: r.data.enabled !== false });
      setViewMode('edit');
    } catch { toast.error('Failed to load template'); }
  };

  const handleSendTest = async () => {
    if (!selected) return;
    setTesting(true);
    try {
      const r = await api.post(`/admin/email-templates/${selected.key}/test`);
      toast.success(`Test email sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send test email');
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const r = await api.put(`/admin/email-templates/${selected.key}`, editForm);
      setSelected(r.data);
      setTemplates(prev => prev.map(t => t.key === selected.key ? { ...t, ...editForm } : t));
      toast.success('Template saved');
    } catch { toast.error('Failed to save template');
    } finally { setSaving(false); }
  };

  const toggleEnabled = async (key, currentVal) => {
    try {
      const r = await api.put(`/admin/email-templates/${key}`, { enabled: !currentVal });
      setTemplates(prev => prev.map(t => t.key === key ? { ...t, enabled: !currentVal } : t));
      if (selected?.key === key) { setSelected(r.data); setEditForm(f => ({ ...f, enabled: !currentVal })); }
      toast.success(!currentVal ? 'Email enabled' : 'Email disabled');
    } catch { toast.error('Failed to update'); }
  };

  const previewHtml = wrap(substitute(editForm.body, PREVIEW_VARS));

  return (
    <DashboardShell title="Notification Manager">
      <p className="text-sm mb-6" style={{ color: 'var(--text-muted)', marginTop: '-12px' }}>
        Manage and customise all transactional email templates. Use <code style={{ fontSize: '12px', backgroundColor: 'var(--bg-subtle)', padding: '1px 4px', borderRadius: '4px' }}>[[variable]]</code> tokens for dynamic content.
      </p>

      <div className="flex gap-6" style={{ minHeight: '600px' }}>
        {/* ── Left: template list ─── */}
        <div className="flex-shrink-0" style={{ width: '280px' }}>
          {loading ? (
            <div className="space-y-2">{[1,2,3,4,5,6].map(i => <div key={i} className="h-16 rounded-xl animate-pulse" style={{ backgroundColor: 'var(--border-clr)' }} />)}</div>
          ) : (
            <div className="space-y-1.5">
              {templates.map(t => {
                const isActive = selected?.key === t.key;
                const dot = EVENT_COLORS[t.key] || '#888';
                return (
                  <button key={t.key} onClick={() => selectTemplate(t.key)} data-testid={`tpl-${t.key}`}
                    className="w-full text-left px-4 py-3 rounded-xl transition-all flex items-center justify-between group"
                    style={{ backgroundColor: isActive ? 'var(--text)' : 'var(--surface)', border: `1px solid ${isActive ? 'var(--text)' : 'var(--border-clr)'}` }}>
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: t.enabled ? dot : 'var(--border-clr)' }} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: isActive ? 'var(--bg)' : 'var(--text)' }}>{t.name}</p>
                        <p className="text-xs truncate mt-0.5" style={{ color: isActive ? 'rgba(255,255,255,0.6)' : 'var(--text-muted)' }}>{t.enabled ? 'Enabled' : 'Disabled'}</p>
                      </div>
                    </div>
                    <ChevronRight size={14} style={{ color: isActive ? 'rgba(255,255,255,0.5)' : 'var(--border-strong)', flexShrink: 0 }} />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* ── Right: editor ─── */}
        <div className="flex-1 min-w-0">
          {!selected ? (
            <div className="h-full flex items-center justify-center rounded-xl border border-dashed" style={{ borderColor: 'var(--border-clr)' }}>
              <div className="text-center">
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>Select a template</p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Choose an email event from the list to edit it</p>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between p-4 rounded-xl border" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}>
                <div>
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{selected.name}</h3>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{selected.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  {/* Enable toggle */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{editForm.enabled ? 'Enabled' : 'Disabled'}</span>
                    <button type="button" data-testid={`toggle-${selected.key}`}
                      onClick={() => toggleEnabled(selected.key, editForm.enabled)}
                      className="relative inline-flex items-center flex-shrink-0 h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none"
                      style={{ backgroundColor: editForm.enabled ? 'var(--text)' : 'var(--border-clr)' }}>
                      <span className="inline-block w-4 h-4 rounded-full bg-white shadow transition-transform duration-200"
                        style={{ transform: editForm.enabled ? 'translateX(1.375rem)' : 'translateX(0.125rem)' }} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Variables reference */}
              {selected.variables?.length > 0 && (
                <div className="flex flex-wrap gap-2 px-1">
                  <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Available variables:</span>
                  {selected.variables.map(v => (
                    <code key={v} className="text-xs px-1.5 py-0.5 rounded cursor-pointer select-all"
                      style={{ backgroundColor: 'var(--bg-subtle)', border: '1px solid var(--border-clr)', color: 'var(--text)', fontFamily: 'monospace' }}>
                      [[{v}]]
                    </code>
                  ))}
                </div>
              )}

              {/* Subject */}
              <div>
                <label className="block text-xs font-medium mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Subject line</label>
                <Input value={editForm.subject} onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))}
                  className="text-sm h-10" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }} data-testid="tpl-subject" />
              </div>

              {/* Body editor / preview toggle */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Email body (HTML)</label>
                  <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'var(--border-clr)' }}>
                    {['edit', 'preview'].map(m => (
                      <button key={m} type="button" onClick={() => setViewMode(m)}
                        className="px-3 py-1 text-xs font-medium capitalize transition-colors"
                        style={{ backgroundColor: viewMode === m ? 'var(--text)' : 'var(--surface)', color: viewMode === m ? 'var(--bg)' : 'var(--text-muted)' }}>
                        {m === 'edit' ? <span className="flex items-center gap-1"><EyeOff size={11} /> Edit</span> : <span className="flex items-center gap-1"><Eye size={11} /> Preview</span>}
                      </button>
                    ))}
                  </div>
                </div>

                {viewMode === 'edit' ? (
                  <textarea
                    value={editForm.body}
                    onChange={e => setEditForm(f => ({ ...f, body: e.target.value }))}
                    rows={18}
                    spellCheck={false}
                    className="w-full rounded-xl p-4 text-sm resize-y focus:outline-none focus:ring-1"
                    style={{ borderColor: 'var(--border-clr)', border: '1px solid var(--border-clr)', backgroundColor: 'var(--surface)', fontFamily: 'monospace', color: 'var(--text)', lineHeight: 1.6 }}
                    data-testid="tpl-body"
                  />
                ) : (
                  <div className="rounded-xl overflow-hidden border" style={{ borderColor: 'var(--border-clr)', height: '420px' }}>
                    <iframe srcDoc={previewHtml} title="Email preview" className="w-full h-full" style={{ border: 'none', backgroundColor: '#fff' }} sandbox="allow-same-origin" />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-between pt-2 border-t gap-3" style={{ borderColor: 'var(--border-clr)' }}>
                <Button
                  variant="outline"
                  onClick={handleSendTest}
                  disabled={testing}
                  style={{ borderRadius: '10px', borderColor: 'var(--border-strong)' }}
                  data-testid="tpl-send-test"
                >
                  <Send size={14} className="mr-2" />{testing ? 'Sending…' : 'Send test email'}
                </Button>
                <Button onClick={handleSave} disabled={saving}
                  style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }} data-testid="tpl-save">
                  <Save size={14} className="mr-2" />{saving ? 'Saving…' : 'Save template'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
